import { Calendar, Hash, Link, Package, Truck, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import useEntity from '../../hooks/useEntity';
import useShippingPartners from '../../hooks/useShippingPartners';
import { AdminOrderService } from '../../services/adminOrderService';
import type { WayuvegaCarrier } from '../../services/adminOrderService';
import { ShippingPartner, ShippingPartnerType } from '../../types/shippingPartner';
import { readLastPackageDimensions, writeLastPackageDimensions } from '../../utils/packageDimensions';

const WAYUVEGA_TENANT_ID = 2739;

interface ShippingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (shippingDetails: {
    trackingId?: string;
    trackingLink?: string;
    expectedDeliveryDate?: string;
    shippingMethod?: 'manual' | 'partner';
    shippingPartner?: string;
    weight?: number;
    height?: number;
    width?: number;
    length?: number;
    carrierPartnerPublicName?: string;
    carrierPartnerAccountCode?: string;
    pickupLocationName?: string;
  }) => void;
  orderId: string;
  isLoading?: boolean;
}

const WAYUVEGA_PARTNER: ShippingPartner = {
  _id: ShippingPartnerType.WAYUVEGA,
  name: 'WayuVega',
  entityId: 0,
  type: ShippingPartnerType.WAYUVEGA,
  enabled: true,
  config: {},
  createdAt: new Date(),
  updatedAt: new Date(),
};

const ShippingDetailsModal: React.FC<ShippingDetailsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  orderId,
  isLoading = false
}) => {
  const [trackingId, setTrackingId] = useState('');
  const [trackingLink, setTrackingLink] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [shippingMethod, setShippingMethod] = useState<'manual' | 'partner'>('manual');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedPickupLocation, setSelectedPickupLocation] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [width, setWidth] = useState('');
  const [length, setLength] = useState('');
  const [wayuvegaCarriers, setWayuvegaCarriers] = useState<WayuvegaCarrier[]>([]);
  const [selectedWayuvegaCarrierId, setSelectedWayuvegaCarrierId] = useState('');
  const [isCheckingWayuvega, setIsCheckingWayuvega] = useState(false);
  const [hasCheckedWayuvega, setHasCheckedWayuvega] = useState(false);
  const [wayuvegaError, setWayuvegaError] = useState('');
  const [prefilledFromLastShipment, setPrefilledFromLastShipment] = useState(false);

  // Fetch shipping partners and entity
  const { data: shippingPartners } = useShippingPartners();
  const { data: entity } = useEntity();
  const isWayuvegaTenant = entity?.entityId === WAYUVEGA_TENANT_ID;

  const automatedPartnerTypes = React.useMemo(() => [
    ShippingPartnerType.DELHIVERY,
    ShippingPartnerType.WAYUVEGA,
    ShippingPartnerType.SHIPROCKET,
    ShippingPartnerType.PARCELHUB
  ], []);

  // Get available and enabled shipping partners
  const availablePartners = React.useMemo(() => {
    const collectionPartners = shippingPartners?.filter(partner =>
      partner.enabled &&
      automatedPartnerTypes.includes(partner.type) &&
      partner.type !== ShippingPartnerType.WAYUVEGA
    ) || [];

    return isWayuvegaTenant
      ? [...collectionPartners, WAYUVEGA_PARTNER]
      : collectionPartners;
  }, [shippingPartners, automatedPartnerTypes, isWayuvegaTenant]);

  const selectedPartnerDetails = React.useMemo(() =>
    availablePartners.find(partner => partner._id === selectedPartner),
    [availablePartners, selectedPartner]);

  const isWayuvegaSelected = selectedPartner === ShippingPartnerType.WAYUVEGA;

  const partnerUsesPickupLocationList = selectedPartnerDetails?.type === ShippingPartnerType.DELHIVERY ||
    selectedPartnerDetails?.type === ShippingPartnerType.SHIPROCKET;
  const availablePickupLocations = React.useMemo(
    () => (partnerUsesPickupLocationList ? selectedPartnerDetails?.config?.pickupLocations || [] : []),
    [partnerUsesPickupLocationList, selectedPartnerDetails]
  );

  // Default to the first configured pickup location whenever the selected partner changes.
  useEffect(() => {
    setSelectedPickupLocation(availablePickupLocations[0] || '');
  }, [availablePickupLocations]);

  const selectedWayuvegaCarrier = React.useMemo(() =>
    wayuvegaCarriers.find(carrier => carrier.carrierPartnerAccountCode === selectedWayuvegaCarrierId),
    [wayuvegaCarriers, selectedWayuvegaCarrierId]);
  const selectedWayuvegaCarrierAccountCode = selectedWayuvegaCarrier?.carrierPartnerAccountCode;
  const selectedPartnerName = isWayuvegaSelected && selectedWayuvegaCarrier
    ? `WayuVega / ${selectedWayuvegaCarrier.carrierPartnerPublicName}`
    : selectedPartnerDetails?.name || 'Partner';

  // Reset shipping method when modal opens
  useEffect(() => {
    if (isOpen) {
      if (availablePartners.length > 0) {
        setShippingMethod('partner');
        setSelectedPartner(availablePartners[0]._id);
      } else {
        setShippingMethod('manual');
        setSelectedPartner('');
      }
    }
  }, [isOpen, availablePartners]);

  // Prefill package dimensions from the last shipment so repeat parcels don't
  // have to be re-measured (and partner rate checks can fire immediately).
  useEffect(() => {
    if (!isOpen) return;

    const lastDimensions = readLastPackageDimensions();
    if (!lastDimensions) {
      setPrefilledFromLastShipment(false);
      return;
    }

    setWeight(lastDimensions.weight);
    setHeight(lastDimensions.height);
    setWidth(lastDimensions.width);
    setLength(lastDimensions.length);
    setPrefilledFromLastShipment(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || shippingMethod !== 'partner' || !isWayuvegaSelected) {
      setWayuvegaCarriers([]);
      setSelectedWayuvegaCarrierId('');
      setWayuvegaError('');
      setHasCheckedWayuvega(false);
      return;
    }

    const parsedWeight = weight ? parseFloat(weight) : undefined;
    const parsedHeight = height ? parseFloat(height) : undefined;
    const parsedWidth = width ? parseFloat(width) : undefined;
    const parsedLength = length ? parseFloat(length) : undefined;
    const dimensions = parsedWeight && parsedHeight && parsedWidth && parsedLength ? {
      weight: parsedWeight,
      height: parsedHeight,
      width: parsedWidth,
      length: parsedLength,
    } : undefined;

    // Only call serviceability/rate when all package dimensions are filled.
    if (!dimensions) {
      setWayuvegaCarriers([]);
      setSelectedWayuvegaCarrierId('');
      setWayuvegaError('');
      setHasCheckedWayuvega(false);
      setIsCheckingWayuvega(false);
      return;
    }

    let isActive = true;
    setIsCheckingWayuvega(true);
    setHasCheckedWayuvega(false);
    setWayuvegaError('');

    const timer = setTimeout(() => {
      if (!isActive) return;
      AdminOrderService.getWayuvegaServiceability(orderId, dimensions)
        .then((result) => {
          if (!isActive) return;
          setWayuvegaCarriers(result.carriers || []);
          setSelectedWayuvegaCarrierId(result.carriers?.[0]?.carrierPartnerAccountCode || '');
          setHasCheckedWayuvega(true);
        })
        .catch((error) => {
          if (!isActive) return;
          setWayuvegaCarriers([]);
          setSelectedWayuvegaCarrierId('');
          setWayuvegaError(error instanceof Error ? error.message : 'Unable to check WayuVega serviceability');
          setHasCheckedWayuvega(true);
        })
        .finally(() => {
          if (isActive) setIsCheckingWayuvega(false);
        });
    }, 500);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [isOpen, shippingMethod, isWayuvegaSelected, orderId, weight, height, width, length]);

  // Editing any dimension means it's no longer the remembered parcel.
  const handleDimensionChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      setPrefilledFromLastShipment(false);
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await onConfirm({
        trackingId: shippingMethod === 'manual' ? (trackingId.trim() || undefined) : undefined,
        trackingLink: shippingMethod === 'manual' ? (trackingLink.trim() || undefined) : undefined,
        expectedDeliveryDate: expectedDeliveryDate || undefined,
        shippingMethod,
        shippingPartner: shippingMethod === 'partner' ? selectedPartner : undefined,
        weight: shippingMethod === 'partner' && weight ? parseFloat(weight) : undefined,
        height: shippingMethod === 'partner' && height ? parseFloat(height) : undefined,
        width: shippingMethod === 'partner' && width ? parseFloat(width) : undefined,
        length: shippingMethod === 'partner' && length ? parseFloat(length) : undefined,
        carrierPartnerPublicName: isWayuvegaSelected ? selectedWayuvegaCarrier?.carrierPartnerPublicName : undefined,
        carrierPartnerAccountCode: isWayuvegaSelected ? selectedWayuvegaCarrierAccountCode : undefined,
        pickupLocationName: partnerUsesPickupLocationList ? (selectedPickupLocation || undefined) : undefined
      });

      // Remember the parcel for the next ship action. Callers swallow their own
      // ship errors, so this also runs on a failed attempt — harmless, since the
      // retry wants the same values anyway.
      if (shippingMethod === 'partner' && weight && height && width && length) {
        writeLastPackageDimensions({ weight, height, width, length });
      }
    } catch (error) {
      console.error('Error submitting shipping details:', error);
    }
  };

  const handleClose = () => {
    setTrackingId('');
    setTrackingLink('');
    setExpectedDeliveryDate('');
    setShippingMethod('manual');
    setSelectedPartner('');
    setSelectedPickupLocation('');
    setWeight('');
    setHeight('');
    setWidth('');
    setLength('');
    setWayuvegaCarriers([]);
    setSelectedWayuvegaCarrierId('');
    setHasCheckedWayuvega(false);
    setWayuvegaError('');
    setPrefilledFromLastShipment(false);
    onClose();
  };

  const hasWayuvegaDimensions = Boolean(weight && height && width && length);
  const isPartnerSubmitDisabled = shippingMethod === 'partner' && (
    !selectedPartner ||
    !weight ||
    !height ||
    !width ||
    !length ||
    (isWayuvegaSelected && (isCheckingWayuvega || !selectedWayuvegaCarrier))
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Truck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Ship Order</h3>
              <p className="text-sm text-gray-500">Order #{orderId}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
            {/* Shipping Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Truck className="w-4 h-4 inline mr-1" />
                Shipping Method
              </label>
              <div className="space-y-3">
                {availablePartners.length > 0 && (
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="partner-shipping"
                      name="shippingMethod"
                      value="partner"
                      checked={shippingMethod === 'partner'}
                      onChange={(e) => setShippingMethod(e.target.value as 'partner')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="partner-shipping" className="ml-3 block text-sm font-medium text-gray-700">
                      <div className="flex items-center">
                        <Package className="w-4 h-4 mr-2 text-blue-600" />
                        Ship with {availablePartners.length === 1 ? availablePartners[0].name : 'Partner'} (Automated)
                      </div>
                      <span className="text-xs text-gray-500">Automatically create shipment and tracking</span>
                    </label>
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="radio"
                    id="manual-shipping"
                    name="shippingMethod"
                    value="manual"
                    checked={shippingMethod === 'manual'}
                    onChange={(e) => setShippingMethod(e.target.value as 'manual')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="manual-shipping" className="ml-3 block text-sm font-medium text-gray-700">
                    <div className="flex items-center">
                      <Truck className="w-4 h-4 mr-2 text-gray-600" />
                      Manual Shipping
                    </div>
                    <span className="text-xs text-gray-500">Enter tracking details manually</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Shipping Partner Selection - Only show if multiple partners */}
            {shippingMethod === 'partner' && availablePartners.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shipping Partner
                </label>
                <select
                  value={selectedPartner}
                  onChange={(e) => setSelectedPartner(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {availablePartners.map((partner) => (
                    <option key={partner._id} value={partner._id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Pickup Location Selection - Only show for partners with multiple configured locations */}
            {shippingMethod === 'partner' && partnerUsesPickupLocationList && availablePickupLocations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pickup Location
                </label>
                <select
                  value={selectedPickupLocation}
                  onChange={(e) => setSelectedPickupLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {availablePickupLocations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Package Dimensions - Only show for partner shipping */}
            {shippingMethod === 'partner' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    <Package className="w-4 h-4 inline mr-1" />
                    Package Dimensions
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Weight */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Weight (gms)
                      </label>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={weight}
                        onChange={handleDimensionChange(setWeight)}
                        placeholder="500"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      />
                    </div>
                    {/* Height */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Height (cm)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={height}
                        onChange={handleDimensionChange(setHeight)}
                        placeholder="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      />
                    </div>
                    {/* Width */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Width (cm)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={width}
                        onChange={handleDimensionChange(setWidth)}
                        placeholder="15"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      />
                    </div>
                    {/* Length */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Length (cm)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={length}
                        onChange={handleDimensionChange(setLength)}
                        placeholder="20"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {prefilledFromLastShipment
                      ? 'Prefilled from your last shipment — edit if this parcel is different. Weight should be in grams.'
                      : 'Enter the package dimensions for accurate shipping calculation. Weight should be in grams.'}
                  </p>
                </div>
              </div>
            )}

            {shippingMethod === 'partner' && isWayuvegaSelected && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WayuVega Carrier
                </label>
                {isCheckingWayuvega || (hasWayuvegaDimensions && !hasCheckedWayuvega) ? (
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    Checking available carriers...
                  </div>
                ) : wayuvegaError ? (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {wayuvegaError}
                  </div>
                ) : wayuvegaCarriers.length > 0 ? (
                  <select
                    value={selectedWayuvegaCarrierId}
                    onChange={(e) => setSelectedWayuvegaCarrierId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {wayuvegaCarriers.map((carrier) => (
                      <option key={carrier.carrierPartnerAccountCode} value={carrier.carrierPartnerAccountCode}>
                        {carrier.carrierPartnerPublicName} — ₹{carrier.totalAmount.toFixed(2)}{carrier.minTatDays != null ? ` (${carrier.minTatDays}-${carrier.maxTatDays} days)` : ''}
                      </option>
                    ))}
                  </select>
                ) : hasWayuvegaDimensions && hasCheckedWayuvega ? (
                  <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
                    No WayuVega carriers are serviceable for this order.
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    Enter package dimensions above to check available carriers.
                  </div>
                )}
              </div>
            )}

            {/* Manual Shipping Fields - Only show for manual shipping */}
            {shippingMethod === 'manual' && (
              <div className="space-y-4">
                {/* Tracking ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Hash className="w-4 h-4 inline mr-1" />
                    Tracking ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    placeholder="e.g., 1234567890, AWB123456, TRK789012"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the tracking number provided by the courier service
                  </p>
                </div>

                {/* Tracking URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Link className="w-4 h-4 inline mr-1" />
                    Tracking URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={trackingLink}
                    onChange={(e) => setTrackingLink(e.target.value)}
                    placeholder="https://example.com/track/123456"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Add a direct tracking link for customers to monitor their shipment
                  </p>
                </div>
              </div>
            )}

            {/* Partner Shipping Info */}
            {shippingMethod === 'partner' && availablePartners.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 mb-1">
                      Automated Shipping
                    </h4>
                    <p className="text-sm text-blue-700">
                      The shipment will be automatically created with {selectedPartnerName}. Tracking details will be updated once the shipment is processed.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Expected Delivery Date - Only show for manual shipping */}
            {shippingMethod === 'manual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Expected Delivery Date (Optional)
                </label>
                <input
                  type="date"
                  value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
                <p className="mt-1 text-xs text-gray-500">
                  When do you expect this order to be delivered?
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 p-4 sm:p-6 border-t border-gray-200 bg-white rounded-b-lg flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || isPartnerSubmitDisabled}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {shippingMethod === 'partner' ? 'Creating Shipment...' : 'Shipping...'}
                </div>
              ) : (
                shippingMethod === 'partner' ? 'Ship with Partner' : 'Ship Order'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShippingDetailsModal; 
